import React from 'react';
import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Menu,
  Flex,
} from '@chakra-ui/react';
import { Icon } from 'src/components/atoms/Icon';
import { MenuButton } from 'src/components/atoms/MenuButton';
import { MenuList } from 'src/components/molecules/MenuList';
import { MenuOption } from 'src/components/atoms/MenuOption';
import { CampaignSearch } from '../CampaignSearch';
import { ModelFormData } from '../../organisms/ModelForm';

import { CampaignMapping } from 'src/api/campaignMappings';
interface MediaPlatform {
  value: string;
  label: string;
}

interface MarketingDataTableProps {
  model: ModelFormData;
  onMediaChange: (modelId: string, index: number, value: string) => void;
  onAddColumn: (modelId: string) => void;
  onRemoveColumn: (modelId: string) => void;
  onCampaignToggle: (
    modelId: string,
    campaignId: string,
    media: string,
  ) => void;
  onSelectAllForMedia: (modelId: string, media: string) => void;
  mediaPlatforms: MediaPlatform[];
  campaignMappings: CampaignMapping[];
  searchedCampaigns: CampaignMapping[];
  searchValue: string;
  setSearchValue: (value: string) => void;
  sortOrder: 'asc' | 'desc' | null;
  handleSortToggle: () => void;
  isAllCampaignsSelectedForMedia: (
    model: ModelFormData,
    media: string,
  ) => boolean;
  hasAvailableMedia: (model: ModelFormData) => boolean;
}

export const MarketingDataTable: React.FC<MarketingDataTableProps> = ({
  model,
  onMediaChange,
  onAddColumn,
  onRemoveColumn,
  onCampaignToggle,
  onSelectAllForMedia,
  mediaPlatforms,
  campaignMappings: _campaignMappings,
  searchedCampaigns,
  searchValue,
  setSearchValue,
  sortOrder,
  handleSortToggle,
  isAllCampaignsSelectedForMedia,
  hasAvailableMedia,
}) => {
  // Process selectedMedia once to avoid double map operations
  const processedMedia = model.selectedMedia
    .map((media, index) => ({ media, index }))
    .filter(({ media }) => media && media !== '');

  // Handler for campaign toggle
  const handleCampaignToggle = (campaignId: number, media: string) => {
    const campaignIdString = campaignId.toString();
    onCampaignToggle(model.id, campaignIdString, media);
  };
  return (
    <Box
      border="1px solid"
      borderColor="border.layout"
      borderRadius="24px"
      p={6}
      bg="surface.card">
      <Text fontSize="16px" fontWeight={600} color="text.title" mb={6}>
        MARKETING DATA
      </Text>

      <Box
        maxH="300px"
        overflowY="auto"
        overflowX="auto"
        border="1px solid"
        borderColor="border.layout"
        borderRadius="md">
        <Table variant="simple" size="sm" minW="800px">
          <Thead position="sticky" top={0} bg="surface.control" zIndex={1}>
            <Tr>
              <Th
                fontSize="14px"
                fontWeight={600}
                color="darkGray"
                py={4}
                minW="180px"
                w="180px">
                <CampaignSearch
                  searchValue={searchValue}
                  onSearchChange={setSearchValue}
                  onSortToggle={handleSortToggle}
                  sortOrder={sortOrder}
                />
              </Th>
              <Th
                fontSize="14px"
                fontWeight={600}
                color="darkGray"
                py={5}
                px={0}
                textAlign="center">
                <Flex align="center" justify="center" gap={1}>
                  <Box
                    as="button"
                    onClick={() => onAddColumn(model.id)}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w="20px"
                    h="20px"
                    bg={hasAvailableMedia(model) ? 'icon.green' : 'gray.400'}
                    color="white"
                    borderRadius="full"
                    fontSize="12px"
                    fontWeight="bold"
                    cursor={
                      hasAvailableMedia(model) ? 'pointer' : 'not-allowed'
                    }
                    _hover={{
                      bg: hasAvailableMedia(model) ? 'green.600' : 'gray.400',
                    }}
                    _active={{
                      bg: hasAvailableMedia(model) ? 'green.700' : 'gray.400',
                    }}
                    disabled={!hasAvailableMedia(model)}
                    _disabled={{
                      bg: 'gray.400',
                      cursor: 'not-allowed',
                      opacity: 0.6,
                    }}>
                    +
                  </Box>
                  <Box
                    as="button"
                    onClick={() => onRemoveColumn(model.id)}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w="20px"
                    h="20px"
                    bg={
                      model.selectedMedia.length > 1 ? 'icon.green' : 'gray.400'
                    }
                    color="white"
                    borderRadius="full"
                    fontSize="12px"
                    fontWeight="bold"
                    cursor={
                      model.selectedMedia.length > 1 ? 'pointer' : 'not-allowed'
                    }
                    _hover={{
                      bg:
                        model.selectedMedia.length > 1
                          ? 'green.600'
                          : 'gray.400',
                    }}
                    _active={{
                      bg:
                        model.selectedMedia.length > 1
                          ? 'green.700'
                          : 'gray.400',
                    }}
                    disabled={model.selectedMedia.length <= 1}
                    _disabled={{
                      bg: 'gray.400',
                      cursor: 'not-allowed',
                      opacity: 0.6,
                    }}>
                    -
                  </Box>
                </Flex>
              </Th>
              {processedMedia.map(({ media, index }) => (
                <Th
                  key={index}
                  fontSize="14px"
                  fontWeight={600}
                  color="darkGray"
                  py={4}
                  textAlign="center"
                  minW="150px"
                  whiteSpace="nowrap">
                  <Menu>
                    <MenuButton
                      buttonName="media-selector"
                      bg="transparent"
                      h="32px"
                      border="none"
                      fontSize="14px"
                      color={media ? 'icon.green' : 'darkGray'}
                      fontWeight={media ? 600 : 400}
                      _hover={{ bg: 'transparent' }}
                      _active={{ bg: 'transparent' }}
                      _focus={{ boxShadow: 'none' }}
                      textAlign="left"
                      justifyContent="space-between"
                      alignItems="center"
                      px={2}
                      w="120px"
                      minW="120px"
                      maxW="120px">
                      <Flex justify="space-between" align="center" w="full">
                        <Text>
                          {mediaPlatforms.find((p) => p.value === media)
                            ?.label ||
                            media ||
                            'Select media'}
                        </Text>
                        <Icon iconName="chevron-down" width={3} height={3} />
                      </Flex>
                    </MenuButton>
                    <MenuList
                      buttonName="media-selector"
                      bg="surface.card"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="border.layout"
                      boxShadow="lg"
                      py={1}
                      maxH="200px"
                      overflowY="auto"
                      overflowX="hidden"
                      w="180px"
                      minW="180px">
                      {mediaPlatforms
                        .filter((platform) => {
                          // Always show "Select media" placeholder, current selection, or available options
                          return (
                            platform.value === '' || // Always show "Select media"
                            platform.value === media ||
                            !model.selectedMedia.includes(platform.value)
                          );
                        })
                        .map((platform) => (
                          <MenuOption
                            key={platform.value}
                            buttonName="media-selector"
                            label={platform.label}
                            value={platform.value}
                            isSelected={platform.value === media}
                            colorSelected={platform.value === media}
                            bg={
                              platform.value === media
                                ? 'icon.green'
                                : 'surface.card'
                            }
                            color={
                              platform.value === media ? 'white' : 'darkGray'
                            }
                            borderRadius="md"
                            mx={1}
                            my={0.5}
                            _hover={{
                              bg: 'icon.green',
                              color: 'white',
                              borderRadius: 'md',
                            }}
                            onClick={() =>
                              onMediaChange(model.id, index, platform.value)
                            }
                          />
                        ))}
                    </MenuList>
                  </Menu>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {/* Select All Row */}
            <Tr>
              <Td py={4}>
                <Text fontSize="16px" fontWeight={500} color="text.title">
                  All
                </Text>
              </Td>
              <Td py={4} textAlign="center">
                {/* Empty cell for + and - buttons column */}
              </Td>
              {processedMedia.map(({ media, index }) => {
                const isAllSelected = isAllCampaignsSelectedForMedia(
                  model,
                  media,
                );

                return (
                  <Td key={index} py={4} textAlign="center">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      cursor="pointer"
                      onClick={() => onSelectAllForMedia(model.id, media)}
                      w="18px"
                      h="18px"
                      margin="0 auto"
                      bg="transparent"
                      border="2px solid"
                      borderColor={isAllSelected ? 'icon.green' : 'gray.300'}
                      color={isAllSelected ? 'icon.green' : 'gray.300'}
                      borderRadius="full"
                      _hover={{
                        opacity: 0.8,
                      }}>
                      {isAllSelected && (
                        <Icon iconName="check" width={2} height={2} />
                      )}
                    </Box>
                  </Td>
                );
              })}
            </Tr>

            {/* Campaign Rows */}
            {searchedCampaigns.map((campaign) => (
              <Tr key={campaign.campaign_id}>
                <Td py={4}>
                  <Text fontSize="16px" fontWeight={500} color="text.title">
                    {campaign.campaign_name}
                  </Text>
                </Td>
                <Td py={4} textAlign="center">
                  {/* Empty cell for + and - buttons column */}
                </Td>
                {processedMedia.map(({ media, index }) => (
                  <Td key={index} py={4} textAlign="center">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      cursor="pointer"
                      onClick={() =>
                        handleCampaignToggle(campaign.campaign_id, media)
                      }
                      w="18px"
                      h="18px"
                      margin="0 auto"
                      bg="transparent"
                      border="2px solid"
                      borderColor={
                        model.campaignSelections[
                          campaign.campaign_id.toString()
                        ]?.[media]
                          ? 'icon.green'
                          : 'gray.300'
                      }
                      color={
                        model.campaignSelections[
                          campaign.campaign_id.toString()
                        ]?.[media]
                          ? 'icon.green'
                          : 'gray.300'
                      }
                      borderRadius="full"
                      _hover={{
                        opacity: 0.8,
                      }}>
                      {model.campaignSelections[
                        campaign.campaign_id.toString()
                      ]?.[media] && (
                        <Icon iconName="check" width={2} height={2} />
                      )}
                    </Box>
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};
